/**
 * Family / household discovery hook (PAC-018).
 *
 * Wraps the Cases service household surface:
 *   - GET  /api/v1/cases/{id}/household  → HouseholdRoster
 *   - POST /api/v1/cases/{id}/household  → HouseholdMember (add a member)
 *
 * Discovering household members is the lever for follow-on cases: an eligible
 * member can spawn its own enrollment case (`creates_case_candidate`). Each
 * member carries relationship, age, eligibility, and a candidate flag so the
 * case page can render a discovery checklist + "creates follow-on case" badge.
 *
 * Degrades gracefully on 404 (endpoint not yet implemented): `available = false`
 * and an empty roster, so the page shows a friendly disabled state.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/** A discovered household / family member. */
export interface HouseholdMember {
  id: string;
  name: string;
  /** Relationship to the index patient, e.g. `spouse`, `child`, `parent`. */
  relationship: string;
  age: number | null;
  /** Whether this member is plausibly eligible for coverage. */
  eligible: boolean;
  /** Whether adding/confirming this member spawns a follow-on enrollment case. */
  creates_case_candidate: boolean;
  notes?: string | null;
}

/** The household roster payload. */
export interface HouseholdRoster {
  members: HouseholdMember[];
}

/** Inbound payload to add a household member (`HouseholdMemberCreate`). */
export interface AddMemberBody {
  name: string;
  relationship: string;
  age?: number | null;
  eligible?: boolean;
  creates_case_candidate?: boolean;
  notes?: string;
}

interface UseHouseholdResult {
  members: HouseholdMember[];
  /** False when the endpoint 404s (surface a disabled empty state). */
  available: boolean;
  isLoading: boolean;
  isWorking: boolean;
  error: ApiError | null;
  actionError: ApiError | null;
  refresh: () => void;
  addMember: (body: AddMemberBody) => Promise<HouseholdMember | null>;
}

/** Loads the household roster and exposes the add-member action. */
export function useHousehold(caseId: string | undefined): UseHouseholdResult {
  const [members, setMembers] = useState<HouseholdMember[]>([]);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    if (!caseId) return;
    setIsLoading(true);
    setError(null);
    apiClient
      .get<HouseholdRoster>(`/cases/${encodeURIComponent(caseId)}/household`)
      .then((roster) => {
        setMembers(roster.members ?? []);
        setAvailable(true);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setAvailable(false);
          setMembers([]);
          setError(null);
          return;
        }
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setMembers([]);
      })
      .finally(() => setIsLoading(false));
  }, [caseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addMember = useCallback(
    async (body: AddMemberBody): Promise<HouseholdMember | null> => {
      if (!caseId) return null;
      setIsWorking(true);
      setActionError(null);
      try {
        const created = await apiClient.post<HouseholdMember>(
          `/cases/${encodeURIComponent(caseId)}/household`,
          body
        );
        if (created) setMembers((prev) => [...prev, created]);
        return created;
      } catch (err: unknown) {
        setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        return null;
      } finally {
        setIsWorking(false);
      }
    },
    [caseId]
  );

  return { members, available, isLoading, isWorking, error, actionError, refresh, addMember };
}
